import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { buildGuards, hasPermission } from '@mint-lib/routing-guards';
import { Campaign } from '@mint-sdk/api-campaign-collections';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import z from 'zod';

const guard = buildGuards(hasPermission('campaignStrategyFeature:view'));

export const Route = createFileRoute('/_secured/campaign/$campaignId/strategy')(
  {
    beforeLoad: async ({ context, params, location }) => {
      const isCampaignIdValid = !Number.isNaN(Number(params.campaignId));
      const isNewStrategyMicroEnabled = await context.isFlagEnabled(
        '@mint/arm',
        'newStrategyMicrofrontend',
      );

      const prefetchQuery = isNewStrategyMicroEnabled
        ? async () => {
            const queryClient = await context.getInstance('queryClient');

            return queryClient.ensureQueryData(
              MediaPlan.getPlanQueryOptions({
                urlParams: { planId: params.campaignId },
              }),
            );
          }
        : () =>
            context.prefetchQuery({
              ...Campaign.getCampaignQueryOptions({
                urlParams: { campaignId: params.campaignId },
              }),
              staleTime: 1000 * 30, // 30 seconds
            });

      if (isCampaignIdValid) {
        try {
          await prefetchQuery();
        } catch {
          throw redirect({
            to: '/campaigns',
          });
        }
      }

      if (!guard(await context.getGuardContext())) {
        throw notFound();
      }

      const isPathDeprecated =
        location.pathname.includes('/strategy/calendar') ||
        location.pathname.includes('/strategy/mediamix') ||
        location.pathname.includes('/strategy/campaign-info');

      if (isNewStrategyMicroEnabled && isPathDeprecated) {
        throw redirect({
          to: '/campaigns',
        });
      }
    },
    loader: async ({ context }) => {
      const isNewStrategyMicroEnabled = await context.isFlagEnabled(
        '@mint/arm',
        'newStrategyMicrofrontend',
      );

      const i18n = await context.getInstance(
        'i18n',
        isNewStrategyMicroEnabled ? '@myn/strategy' : '@myn/strategy_v2',
      );

      await context.getInstance('i18n', '@mint-lib/labels-table');

      const title = i18n.t('Strategy | MINT');
      return { title };
    },
    validateSearch: z.object({
      hierarchy: z.string().optional(),
    }),
    head: ({ loaderData }) => ({
      meta: [
        {
          title: loaderData?.title,
        },
      ],
    }),
  },
);
